import React from "react";
import Modal from "react-modal";
import classNames from "classnames";
import PropTypes from "prop-types";
import CloseButton from "../CloseButton";
import { motion, AnimatePresence } from "framer-motion";
import { theme } from "twin.macro";
import useWindowSize from "../hooks/useWindowSize";
import Button from "../Buttons";

const colorClassMap = {
  red: "bg-red-600 hover:bg-red-700 text-white",
  green: "bg-green-600 hover:bg-green-700 text-white",
  blue: "bg-blue-600 hover:bg-blue-700 text-white",
  gray: "bg-gray-600 hover:bg-gray-700 text-white",
};

const getConfirmClass = (color) => colorClassMap[color] || colorClassMap.blue;

const Dialog = (props) => {
  const currentSize = useWindowSize();

  const {
    children,
    className,
    closable,
    width,
    height,
    style,
    isOpen,
    onClose,
    onConfirm,
    title,
    icon,
    confirmButtonText,
    confirmButtonColor,
    cancelButtonText,
    bodyOpenClassName,
    portalClassName,
    overlayClassName,
    contentClassName,
    closeTimeoutMS,
    footer,
    loading,
    ...rest
  } = props;

  const onCloseClick = (e) => {
    if (onClose && !loading) onClose(e);
  };

  const renderCloseButton = (
    <CloseButton
      onClick={onCloseClick}
      className="ltr:right-4 rtl:left-4"
      absolute
      aria-label="Close dialog"
      disabled={loading}
    />
  );

  const renderFooterButtons = (
    <div className="flex items-center justify-end gap-3 w-full">
      <Button variant="outline" onClick={onClose} disabled={loading}>
        {cancelButtonText || "Cancel"}
      </Button>
      <Button
        variant="solid"
        onClick={onConfirm}
        className={getConfirmClass(confirmButtonColor)}
        loading={loading}
        disabled={loading}
      >
        {confirmButtonText || "Confirm"}
      </Button>
    </div>
  );

  // compute content style
  const contentStyle = {
    content: {
      inset: "unset",
      border: "none",
      background: "transparent",
      padding: 0,
    },
    ...style,
  };

  if (width !== undefined) {
    const w = typeof width === "number" ? `${width}px` : width;
    contentStyle.content.width = w;

    try {
      const smPx = parseInt(
        theme`screens.sm`.split(/ /)[0].replace(/[^\d]/g, "")
      );
      if (currentSize.width <= smPx) {
        contentStyle.content.width = "auto";
      }
    } catch (e) {
      // fallback: keep width as set
    }
  }
  if (height !== undefined) {
    contentStyle.content.height = height;
  }

  const defaultDialogContentClass = "dialog-content";

  const dialogClass = classNames(
    "bg-white dark:bg-slate-900 rounded-lg shadow-xl overflow-hidden",
    "max-w-full",
    contentClassName,
    defaultDialogContentClass,
    className
  );

  const headerClass =
    "flex items-center justify-between px-5 py-3 border-b dark:border-slate-800";
  const bodyClass =
    "flex-1 overflow-y-auto px-5 py-4 text-sm text-slate-700 dark:text-slate-300";

  const footerClass =
    "px-5 py-3 border-t dark:border-slate-800 bg-white/50 dark:bg-slate-900/50";

  return (
    <Modal
      className={{
        base: classNames(
          "dialog fixed inset-0 flex items-center justify-center z-50"
        ),
        afterOpen: "dialog-after-open",
        beforeClose: "dialog-before-close",
      }}
      overlayClassName={{
        base: classNames(
          "dialog-overlay fixed inset-0 bg-black/40 backdrop-blur-sm",
          overlayClassName
        ),
        afterOpen: "dialog-overlay-after-open",
        beforeClose: "dialog-overlay-before-close",
      }}
      portalClassName={classNames("dialog-portal", portalClassName)}
      bodyOpenClassName={classNames("dialog-open", bodyOpenClassName)}
      ariaHideApp={false}
      isOpen={isOpen}
      onRequestClose={onCloseClick}
      shouldCloseOnOverlayClick={closable && !loading} // 🔥 Prevent closing on click outside if loading
      shouldCloseOnEsc={closable && !loading} // 🔥 Prevent closing on Esc key if loading
      style={{ ...contentStyle }}
      closeTimeoutMS={closeTimeoutMS}
      {...rest}
      aria={{
        modal: true,
        labelledby: title ? "dialog-title" : undefined,
      }}
    >
      <AnimatePresence>
        {isOpen && (
          <motion.div
            role="dialog"
            className={dialogClass}
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            style={{
    width: contentStyle.content?.width || "auto",
    maxWidth: "96vw",
    maxHeight: "90vh",   // <-- add
    display: "flex",     // <-- add
    flexDirection: "column", // <-- add
  }}
          >
            {(title || closable || icon) && (
              <div className={headerClass}>
                <div className="flex items-center gap-3 min-w-0">
                  {icon && <div className="flex-none">{icon}</div>}
                  {title && (
                    <h3
                      id="dialog-title"
                      className="text-lg font-semibold truncate"
                    >
                      {title}
                    </h3>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {closable && renderCloseButton}
                </div>
              </div>
            )}

            <div className={bodyClass}>{children}</div>

            {footer ? (
              <div className={footerClass}>{footer}</div>
            ) : (
              onConfirm && (
                <div className={footerClass}>{renderFooterButtons}</div>
              )
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Modal>
  );
};

Dialog.propTypes = {
  closable: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  overlayClassName: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  icon: PropTypes.node,
  confirmButtonText: PropTypes.string,
  confirmButtonColor: PropTypes.string,
  cancelButtonText: PropTypes.string,
  portalClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  closeTimeoutMS: PropTypes.number,
  bodyOpenClassName: PropTypes.string,
  footer: PropTypes.node,
};

Dialog.defaultProps = {
  closable: true,
  width: 520,
  closeTimeoutMS: 150,
  confirmButtonColor: "red",
  loading: false,
};

export default Dialog;
